import ModalTemplate from "@/components/ModalTemplate/ModalTemplate";
import InformationBlock from "@/components/InformationBlock/InformationBlock";

import { AppointmentDTO } from "@/models/AppointmentDTO";
import { McsisRole } from "@/models/constants/McsisRole";
import AppointmentService from "@/services/appointment.service";
import { ActionTypes, GetterTypes } from "@/store-vuex/types";
import { Component, Vue, Watch } from "vue-property-decorator";
import { AppointmentStatus, Sex } from "@/models/constants/enums";
import moment from "moment";

import {
  isEditDeleteAllowed,
  getPeriodTimeBeforeApt,
  isEditDeleteAllowedDuringClosedPeriod
} from "@/config/app.config";
import AppointmentRecap from "@/components/AppointmentRecap/AppointmentRecap";
import { McsisConstants } from "@/models/constants/McsisConstants";
import router from "@/router";
import AppointmentDetails from "../Appointment-Details/AppointmentDetails";
import { AppointmentDetailsDTO } from "@/models/AppointmentDetailsDTO";
import { PublicAppointmentParticipantRequest } from "@/models/PublicAppointmentParticipantRequest";
import { PublicAppointmentParticipantResponse } from "@/models/PublicAppointmentParticipantResponse";
import { ParticipantDTO } from "@/models/ParticipantDTO";

@Component({
  components: {
    AppointmentRecap,
    ModalTemplate,
    InformationBlock
  }
})
export default class Recap extends Vue {
  private email = "";
  private showModal = false;
  private modalTextKey = "";
  private isDeleteEditAllowed = false;
  private isMissedRdv = false;
  private appointmentStatus = AppointmentStatus;
  private deleteEditInfos = "page.recap.forbidDeleteEditInfos";
  private infoTextKey = "";
  private showSlotTakenModal = false;
  private isContactFormInvalid = true;

  //#region ----------- Getter, Watcher & Hooks -------------

  private get isModified(): boolean {
    return this.$store.getters[GetterTypes.isRdvModified];
  }

  private get isEdition(): boolean {
    return this.$store.getters[GetterTypes.hasRdv];
  }

  private get appointment(): AppointmentDTO {
    return this.$store.getters[GetterTypes.appointment];
  }

  private get isAppointmentInThePast(): boolean {
    return this.appointment.appDate !== null ? new Date(this.appointment.appDate) < new Date() : false;
  }

  public onValidityCheck(value: boolean): void {
    this.isContactFormInvalid = !value;
  }

  public mounted() {
    this.isDeleteEditAllowed = this.computeIsDeleteEditAllowed();
    this.isMissedRdv = this.computeIsMissedRdv();
    this.infoTextKey = this.computeInfoTextKey();
  }

  /**
   * Get the ParticipantDTO instance
   */
  public get participant(): ParticipantDTO {
    return this.$store.getters[GetterTypes.participant];
  }

  /**
   * Gets true if AppointmentParticipant step must be displayed
   *
   * @returns {boolean} true or false
   */
  public get showAppointmentParticipantStep(): boolean {
    return this.$store.getters[GetterTypes.showAppointmentParticipantStep];
  }

  //#endregion
  //#region ---------------- Service Handlers -------------

  /**
   * crée un rdv, prépare le pdf et l'email et redirige ver la page de confirmation
   * @param appointment
   */
  protected async createAppointment(
    appointment: AppointmentDTO
  ): Promise<void> {
    await AppointmentService.CreateAppointment(appointment).then(
      async (res: AppointmentDTO | null) => {
        if (res != null) {
          this.$store.commit(ActionTypes.setAppointment, res);

          let publicAppointmentParticipantRequest: PublicAppointmentParticipantRequest | null = null;

          if (this.showAppointmentParticipantStep) {
            publicAppointmentParticipantRequest = {
              id: 0,
              screeningUnitId: this.participant.suId,
              participantId: this.participant.partId,
              appointmentId: res.appId as number,
              firstName: this.participant.firstName,
              lastName: this.participant.lastName,
              birthDate: this.participant.birthDate,
              localityId: this.participant.locality
                ? (this.participant.locality.locId as number)
                : 0,
              mainAddress: this.participant.mainAddressLine1,
              email: this.participant.email,
              mobilePhoneNumber: this.participant.phoneNumber,
              doctorId: this.participant.doctor
                ? this.participant.doctor.doctorId
                : null,
              doctorLastName: !this.participant.doctor
                ? this.participant.doctorLastname
                : null,
              doctorFirstName: !this.participant.doctor
                ? this.participant.doctorFirstname
                : null,
              doctorAddress: !this.participant.doctor
                ? this.participant.doctorAddress
                : null,
              doctorLocality: !this.participant.doctor
                ? this.participant.doctorLocality
                : null,
              doctorPhoneNumber: !this.participant.doctor
                ? this.participant.doctorPhone
                : null,
              insuranceId: this.participant.insurance
                ? (this.participant.insurance.personId as number)
                : 0,
              avsNumber: this.participant.avsNumber,
              treated: false,
              archived: false
            };
          }

          if (
            publicAppointmentParticipantRequest !== null &&
            publicAppointmentParticipantRequest !== undefined
          ) {
            await AppointmentService.GetAppointmentByPartId(publicAppointmentParticipantRequest.participantId).then(
              async (res: AppointmentDTO) => {
                if (res && res.appId && publicAppointmentParticipantRequest) {
                  publicAppointmentParticipantRequest.appointmentId = res.appId;
                  await AppointmentService.createPublicAppointmentParticipant(
                    publicAppointmentParticipantRequest as PublicAppointmentParticipantRequest
                  ).then(
                    async (
                      response: PublicAppointmentParticipantResponse | null
                    ) => {
                      if (response != null) {
                        this.routeToConfirmationPage();
                      }
                    }
                  );
                }
              }
            );
          } else {
            this.routeToConfirmationPage();
          }
        } else {
          this.showSlotTakenModal = true;
        }
      }
    );
  }

  /**
   * @brief modifie un rdv, prépare le pdf et l'email et redirige ver la page de confirmation
   * @param appointment
   */
  protected async editAppointment(
    appointment: AppointmentDTO,
    isValidation: boolean = false
  ): Promise<void> {
    if (isValidation) {
      appointment.needAvailableSlot = false;
    } else {
      appointment.needAvailableSlot = true;
    }
    await AppointmentService.EditAppointment(appointment)
      .then(async () => {
        if (isValidation) {
          this.$store.commit(ActionTypes.setAppointment, appointment);
          this.$store.commit(ActionTypes.setIsRdvModified, false);
        } else {
          this.$store.commit(ActionTypes.setAppointment, appointment);
        }

        let publicAppointmentParticipantRequest: PublicAppointmentParticipantRequest | null = null;

        if (this.showAppointmentParticipantStep) {

          await AppointmentService.getPublicAppointmentParticipant(
            this.participant.suId,
            this.appointment.partId as number,
            this.appointment.appId as number
          ).then(
            async (response: PublicAppointmentParticipantResponse | null) => {
              if (response !== null && response !== undefined) {
                publicAppointmentParticipantRequest = {
                  id: response.id,
                  screeningUnitId: response.screeningUnitId,
                  participantId: response.participantId,
                  appointmentId: response.appointmentId,
                  firstName: this.participant.firstName,
                  lastName: this.participant.lastName,
                  birthDate: this.participant.birthDate,
                  localityId: this.participant.locality
                    ? (this.participant.locality.locId as number)
                    : 0,
                  mainAddress: this.participant.mainAddressLine1,
                  email: this.participant.email,
                  mobilePhoneNumber: this.participant.phoneNumber,
                  doctorId: this.participant.doctor
                    ? this.participant.doctor.doctorId
                    : null,
                  doctorLastName: !this.participant.doctor
                    ? this.participant.doctorLastname
                    : null,
                  doctorFirstName: !this.participant.doctor
                    ? this.participant.doctorFirstname
                    : null,
                  doctorAddress: !this.participant.doctor
                    ? this.participant.doctorAddress
                    : null,
                  doctorLocality: !this.participant.doctor
                    ? this.participant.doctorLocality
                    : null,
                  doctorPhoneNumber: !this.participant.doctor
                    ? this.participant.doctorPhone
                    : null,
                  insuranceId: this.participant.insurance
                    ? (this.participant.insurance.personId as number)
                    : 0,
                  avsNumber: this.participant.avsNumber,
                  treated: false,
                  archived: false
                };

                await AppointmentService.editPublicAppointmentParticipant(
                  publicAppointmentParticipantRequest
                ).then(() => {
                  this.routeToConfirmationPage();
                });
              }
            }
          );
        } else {
          this.routeToConfirmationPage();
        }
      })
      .catch(() => {
        this.showSlotTakenModal = true;
      });
  }

  /**
   * @brief supprime un rdv, prépare le pdf et l'email et redirige ver la page de confirmation
   * @param appId
   */
  protected async deleteAppointment(appId: number): Promise<void> {
    await AppointmentService.DeleteAppointment(appId).then(() => {
      this.$store.commit(ActionTypes.setAppointment, {} as AppointmentDTO);
      this.$router.push({
        name: "confirmation",
        params: {
          screening: this.$route.params.screening
        }
      });
    });
  }

  //#endregion
  //#region ---------------- Events Handlers -------------

  /**
   * @brief crée un nouveau appointment ou confirme un rdv alloué automatiquement
   */
  protected async onSubmit(isValidation: boolean = false): Promise<void> {
    router.app.$store.dispatch(ActionTypes.setDataLoading, true);
    await this.$isInRole(McsisRole.Participant).then(
      async (isInRole: boolean) => {
        if (isInRole) {
          if (this.isEdition) {
            await this.editAppointment(this.appointment, isValidation);
          } else {
            await this.createAppointment(this.appointment);
          }
        }
      }
    );
    router.app.$store.dispatch(ActionTypes.setDataLoading, false);
  }

  /**
   * @brief supprimer ou modifie un rendez vous après la confirmation de la modale
   */
  protected async onConfirm(): Promise<void> {
    router.app.$store.dispatch(ActionTypes.setDataLoading, true);
    this.showModal = false;

    await this.$isInRole(McsisRole.Participant).then(
      async (isInRole: boolean) => {
        if (isInRole) {
          if (this.isModified) {
            await this.editAppointment(this.appointment);
          } else {
            await this.deleteAppointment(
              this.$store.getters[GetterTypes.appID]
            );
          }
        }
      }
    );

    router.app.$store.dispatch(ActionTypes.setDataLoading, false);
  }

  protected onCancel(): void {
    this.$store.dispatch(ActionTypes.setIsDeleted, false);
    this.showModal = false;
  }

  protected onDelete(): void {
    this.$store.dispatch(ActionTypes.setIsDeleted, true);
    this.modalTextKey = "page.recap.deleteModal";
    this.showModal = true;
  }

  protected onValidate(): void {
    this.modalTextKey = "page.recap.editionModal";
    this.showModal = true;
  }

  protected onUpdate(): void {
    this.appointment.appSlotNumber = 1;
    (this.appointment.appointmentDetails = {
      appdId: undefined,
      appId: this.appointment.appId,
      trmGenderId: Sex.Undefined,
      isLongAppointment: false,
      longAppointmentReasonsId: []
    } as AppointmentDetailsDTO),
      this.$router.push({
        name: "appointment-details",
        params: {
          screening: this.$route.params.screening
        }
      });
  }

  //#endregion
  //#region ---------------- Helpers -------------

  /**
   * @brief calucle la bonne clé de texte selon le mode
   * @returns la clé du texte
   */
  private computeInfoTextKey(): string {
    if (!this.isEdition) {
      return "";
    }

    if (this.isModified) {
      return "page.recap.modificationInfos";
    }

    if (this.appointment.vappsId === AppointmentStatus.Booked) {
      if (this.isDeleteEditAllowed) {
        return "page.recap.autoAllocWithEditInfos";
      } else {
        return "page.recap.autoAllocInfos";
      }
    } else if (this.isDeleteEditAllowed) {
      return "page.recap.editionInfos";
    } else {
      return this.deleteEditInfos;
    }
  }

  /**
   * @brief calucle la bonne clé de texte selon le mode et si on peut modifier ou non dans ce mode
   * @returns true si on peut modifier/supprimer
   */
  private computeIsDeleteEditAllowed(): boolean {
    const today = moment();

    if (this.appointment.appDate == null) {
      return false;
    }

    if (!isEditDeleteAllowed(this.$route.params.screening)) {
      return false;
    }

    const limitDate = moment(this.appointment.appDate).subtract(
      getPeriodTimeBeforeApt(this.$route.params.screening),
      McsisConstants.SubstractDaysParameter
    );

    if (
      today.format(McsisConstants.DefaultDateFormat) >
      limitDate.format(McsisConstants.DefaultDateFormat)
    ) {
      this.deleteEditInfos = "page.recap.forbidDeleteEditXDayBeforeInfos";
      return false;
    }

    if (!isEditDeleteAllowedDuringClosedPeriod(this.$route.params.screening)) {
      const lastPrint =
        this.appointment.center != null &&
        this.appointment.center.ecLastPrint != null
          ? moment(this.appointment.center.ecLastPrint)
          : moment();

      if (moment(this.appointment.appDate) <= lastPrint) {
        this.deleteEditInfos =
          "page.recap.forbidDeleteEditDuringClosedPeriodInfos";
        return false;
      }
    }

    return true;
  }

  /**
   * @brief vérifie s'il s'agit d'un rdv manqué et adapte le texte en conséquence
   * @returns true s'il s'agit d'un rdv manqué
   */
  private computeIsMissedRdv(): boolean {
    if (
      this.appointment.appDate != null &&
      moment(this.appointment.appDate) < moment()
    ) {
      this.deleteEditInfos = "page.recap.missedRdv";
      return true;
    }

    return false;
  }

  private async routeToConfirmationPage() {
    this.$router.push({
      name: McsisConstants.AppointmentConfirmationPage,
      params: {
        screening: this.$route.params.screening
      }
    });
  }
  //#endregion
}
